import { type EventContentArg } from "@fullcalendar/core"
import { Flex, Typography } from "antd"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import { CalendarTimeAndType } from "../CalendarTimeAndType.tsx"

import {
  calendarEventCardAddress,
  calendarEventCardRecordParts,
  calendarEventCardText
} from "../../helpers/calendar-event-card-text.ts"

const { Text } = Typography

interface CalendarEventCellEventProps {
  eventInfo: EventContentArg
}

const TypeDot = ({ color }: { color?: string }) => (
  <span
    className="inline-block shrink-0 size-1.5 rounded-full"
    style={{ backgroundColor: color }}
  />
)

export function CalendarEventCellEvent({
  eventInfo
}: CalendarEventCellEventProps) {
  const { t } = useTranslation()
  const event = eventInfo?.event
  const input = { title: event?.title, record: event?.extendedProps }
  const { title, typeLabel } = calendarEventCardText(input, t)
  const { recordName, personName } = calendarEventCardRecordParts(input, t)
  // Person and location share the card's fourth line — whichever the event
  // actually has, never both — so the card never grows past four lines
  // regardless of duration (a fifth line was overflowing the event's own
  // box in a real browser; jsdom can't see that, so it wasn't caught until
  // manual verification).
  const personOrAddress = personName || calendarEventCardAddress(input)

  // The time always rides on the SAME line as the title — never a separate
  // stacked line — so a default 15-minute Follow-Up still shows it. The
  // record (lead/project) and person/address lines only add once a slot is
  // 30 minutes or longer: @slot-height in calendar.less was raised
  // specifically so a 30-minute box has room for all 3 lines (title+time,
  // record, person/address) without one clipping into the next (confirmed
  // live). Below 30 minutes there still isn't room for more than the one
  // line.
  const diffInMinutes = dayjs(event?.end).diff(dayjs(event?.start), "minute")
  const showDetails = diffInMinutes >= 30
  // A 60-minute-or-longer slot (two grid rows or more, see calendar.less
  // @slot-height) has room to read larger without pushing past 3 lines —
  // sized for the tightest 30-minute case otherwise.
  const hasExtraRoom = diffInMinutes >= 60
  const titleSizeClass = hasExtraRoom ? "text-[14px]" : "text-[12px]"
  const timeSizeClass = hasExtraRoom ? "text-[12px]" : "text-[10px]"
  const detailSizeClass = hasExtraRoom ? "text-[13px]" : "text-[11px]"
  // leading-none sizes each line's box to exactly its font-size with zero
  // slack, so with no gap a real browser's glyph rendering reads as one
  // line touching the next (reported live on a 60-minute card, invisible
  // in jsdom). A larger slot can spend more of its spare height on this.
  const lineGap = hasExtraRoom ? 8 : 2

  // Struck through rather than hidden: whoever was invited needs to see that
  // the meeting was cancelled, not find it silently gone from the grid.
  const isCancelled = event?.extendedProps?.status === "CANCELLED"

  // Month cells are a fraction of a week/day slot's height, so they get one
  // line.
  const isMonthView = eventInfo?.view?.type === "dayGridMonth"
  // FullCalendar's list view already renders the time and a colored dot in
  // its own cells (`.fc-list-event-time` / `-graphic`) — eventContent here
  // only fills the title cell, so this is title + a compact Lead/Project ·
  // participants · status line, not a rebuild of the whole row.
  const isListView = !!eventInfo?.view?.type?.startsWith("list")

  if (isListView) {
    const participantNames = event?.extendedProps?.participants
      ?.map((p: { full_name?: string }) => p?.full_name)
      .filter(Boolean)

    return (
      <Flex
        vertical
        gap={2}
        className={isCancelled ? "line-through opacity-60" : ""}
        data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
      >
        <Flex align="center" gap={6}>
          <Text strong className="text-sm">
            {title}
          </Text>
          {!!typeLabel && (
            <Text type="secondary" className="text-[11px] shrink-0">
              {typeLabel}
            </Text>
          )}
          {isCancelled && (
            <Text type="danger" className="text-[11px] font-medium">
              Cancelled
            </Text>
          )}
        </Flex>
        {!!(recordName || personOrAddress || participantNames?.length) && (
          <Text type="secondary" className="text-xs">
            {[recordName, personOrAddress, participantNames?.join(", ")]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        )}
      </Flex>
    )
  }

  if (isMonthView) {
    return (
      <Flex
        align="center"
        gap={4}
        className={`overflow-hidden size-full ${isCancelled ? "line-through opacity-60" : ""}`}
        data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
      >
        <TypeDot color={event?.borderColor} />
        {eventInfo?.timeText && (
          <Text className="text-xs text-inherit opacity-80 leading-4 shrink-0">
            {eventInfo.timeText}
          </Text>
        )}
        <Text ellipsis className="text-xs text-inherit font-medium leading-4">
          {title}
        </Text>
      </Flex>
    )
  }

  // Title first, time second: the title is what a reader scans for, and the
  // event's own duration/color already anchor "when" spatially on the grid.
  // Time previously rendered ABOVE the title, which put the taller, bolder
  // title line in the space the shorter box had already spent on the time
  // line above it — confirmed live as the exact cause of the two lines
  // visually colliding on a 30-minute slot.
  //
  // `leading-none` (line-height: 1) was tried first to keep each line's box
  // as close to its font size as possible, but a single-line `Text` clips
  // at exactly that box height, and a real browser's glyph metrics need
  // slightly more than 1x font-size to show a descender in full — "Meeting"
  // rendered with its "g" cut off (confirmed live; invisible in jsdom).
  // `leading-[1.2]` gives every line just enough headroom for descenders
  // without the line boxes overlapping — calendar.less's @slot-height
  // accounts for the difference.
  return (
    <Flex
      vertical
      gap={lineGap}
      className={`overflow-hidden size-full ${isCancelled ? "line-through opacity-60" : ""}`}
      data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
    >
      <Flex align="center" gap={4} className="min-w-0">
        <TypeDot color={event?.borderColor} />
        {/* No gap here — the title and time must sit right next to each
            other, separated only by the ": " already baked into the time
            text. The wrapper (not the title) is what stretches to fill the
            row, so a short title doesn't drag the time to the far edge. */}
        <Flex align="baseline" className="min-w-0 flex-1">
          <Text
            ellipsis
            className={`${titleSizeClass} text-inherit font-semibold leading-[1.2] min-w-0 shrink`}
          >
            {title}
          </Text>
          <CalendarTimeAndType
            className={`${timeSizeClass} opacity-75 leading-[1.2] ${eventInfo?.timeText ? "" : "ml-1"}`}
            time={eventInfo?.timeText ? `: ${eventInfo.timeText}` : null}
            typeLabel={typeLabel}
          />
        </Flex>
      </Flex>

      {recordName && showDetails && (
        <Text ellipsis className={`${detailSizeClass} text-inherit opacity-90 leading-[1.2]`}>
          {recordName}
        </Text>
      )}

      {personOrAddress && showDetails && (
        <Text ellipsis className={`${detailSizeClass} text-inherit opacity-70 leading-[1.2]`}>
          {personOrAddress}
        </Text>
      )}
    </Flex>
  )
}
